/**
 * @author       tae@zumper.com (Tae Yoon)
 * @copyright    Copyright (c) 2020, Zumper
 * @description  TourButtonContainer
 */

import {
  getUser,
  selectIsMobile,
  selectIsSendingTour,
  setSendingTour,
} from '@zcode-ui/web-zumper-global-state'
import {
  CLICK_TOUR_AVAILABILITY,
  TOUR_BOOKING_SUBMIT_CLICK,
  UNDO_TOUR,
  event,
  getEntityProperties,
} from '@zcode-ui/web-zumper-integrations'
import type { Unit } from '@zcode/bizos'
import { EVENT_NAMES, PROP_NAMES, TOUR_BUTTON_TYPES, track } from '@zcode/mixpanel'
import { type AnalyticsC, Origin } from '@zcode/nr-utils'
import { MESSAGE, TOUR } from '@zcode/nr/es/shared/constants/message'
import { hocCompose, memoMapDispatchToProps as memo } from '@zcode/react-utils'
import { withRouter } from '@zcode/router-utils'
import type { RouteComponentProps } from '@zcode/router-utils'
import type {
  FloorplanListing,
  Listable,
  ListableBuilding,
  Listing,
  MaybeEmptyProperty,
  NormalFloorplanListingData,
  Pin,
  Property,
  RawPinInventorySearch,
  State,
} from '@zcode/state-type'
import { type InferableComponentEnhancerWithProps, connect } from 'react-redux'
import type { Dispatch } from 'redux'
import invariant from 'tiny-invariant'
import { isEqualTourMessaged } from '../../../inspectors/index'
import { setLastClickedEntity } from '../../../modules/currentSearch/actions/index'
import { selectEntity } from '../../../modules/detail/modules/entity/selectors/index'
import { selectFilterBedroom } from '../../../modules/filters/selectors/index'
import { getCommonProps } from '../../../modules/mixpanel/helpers/getCommonProps'
import { getPageNameFromRouteProps } from '../../../modules/mixpanel/helpers/getPageNameFromRouteProps'
import { getPreviousPage } from '../../../modules/mixpanel/helpers/previousPageLocalStorage'
import { mixpanelClickTourAvailability } from '../../../modules/mixpanel/thunks/mixpanelClickTourAvailability'
import { default as fetchMessagingEntity } from '../../../modules/modals/thunks/messageModal/fetchMessagingEntity'
import { openMessageModalWithIntent } from '../../../modules/modals/thunks/messageModal/openMessageModalWithIntent'
import { receiveSelectedDatetime } from '../../../modules/multifamilyTourTimePicker/actions/index'
import {
  selectFetchingTourIntegrationForGroupId,
  selectHasSelectedTime,
  selectIsFetchingAnyTourIntegration,
} from '../../../modules/multifamilyTourTimePicker/selectors/index'
import { createMultifamilyTourRequest } from '../../../modules/multifamilyTourTimePicker/thunks/createMultifamilyTourRequest'
import { getTraktorPageType } from '../../../modules/traktor/thunks/trackDetailEvent'
import { trackTourBookingEvent } from '../../../modules/traktor/thunks/trackTourBookingEvent'
import {
  getDayInTz,
  getNextPossibleTimeOfficeHours,
  getOneDayAhead,
  getTwoDaysAhead,
} from '../../message/MessageForm/TourAvailabilityPicker/helpers'
import { getHasInstantTourAvailability } from '../../message/MessageForm/TourOptions/getHasInstantTourAvailability'
import { TourButton, type TourButtonProps } from './TourButton'
import type { getOneTapTourData } from './getOneTapTourData'

interface TourButtonContainerProps extends RouteComponentProps {
  analyticChannel:
    | typeof AnalyticsC.AFFORDABILITY_CALCULATOR
    | typeof AnalyticsC.FULL_DETAIL
    | typeof AnalyticsC.LIST
    | typeof AnalyticsC.LIST_ITEM
    | typeof AnalyticsC.LIST_ITEM_MESSAGED
    | typeof AnalyticsC.PERMANENT_ADDRESS
    | typeof AnalyticsC.PROGRAMMATIC_RENT
    | typeof AnalyticsC.HOMEPAGE
  entity?: Listable | Property | Pin<RawPinInventorySearch> | null | undefined
  extraPayload?: { currentTab?: string }
  isMessaged?: boolean
  onClick?: () => void
  origin: Origin
  selectedFloorplan?: FloorplanListing<NormalFloorplanListingData>
  selectedUnit?: Unit
}

export const mapStateToProps = (state: State, ownProps: TourButtonContainerProps) => {
  const entity = ownProps.entity || selectEntity(state)
  const { isExternal, isMessageable, isTourDisabled } = entity || {}

  return {
    bedroomsArray: selectFilterBedroom(state),
    isExternal,
    isFetchingAnyTourIntegration: selectIsFetchingAnyTourIntegration(state),
    isFetchingThisTourIntegration:
      !!entity?.groupId && selectFetchingTourIntegrationForGroupId(state, entity.groupId),
    isMessageable,
    isMessaged: ownProps.isMessaged ?? isEqualTourMessaged(state, entity),
    isSendingTour: selectIsSendingTour(state),
    isTourDisabled,
  } satisfies Partial<TourButtonProps>
}

const mapDispatchToProps = (dispatch: Dispatch, ownProps: TourButtonContainerProps) => {
  return {
    fetchMessagingEntity: async (
      listable: Listing | ListableBuilding | Pin<RawPinInventorySearch>
    ) => {
      const { buildingId, listingId, isBuilding } = listable
      if (
        isBuilding &&
        ownProps.origin !== Origin.PA_TABLE_TOUR &&
        ownProps.origin !== Origin.ADDR_PA_TABLE_TOUR
      ) {
        return await dispatch(fetchMessagingEntity({ buildingId }))
      }
      if (listingId) {
        return await dispatch(fetchMessagingEntity({ listingId }))
      }
    },
    getHasInstantTourAvailability: (entity: Listable | Property | null | undefined) => {
      return dispatch(getHasInstantTourAvailability(entity))
    },
    onClick: ({
      hasInstantTourAvailability,
      messageEntity,
    }: {
      hasInstantTourAvailability: boolean
      messageEntity: Listable | Property | null | undefined
    }) => {
      const {
        analyticChannel,
        origin,
        selectedFloorplan,
        selectedUnit,
        onClick: ownPropsOnClick,
      } = ownProps
      ownPropsOnClick?.()

      dispatch((_, getState) => {
        const state = getState()
        const entity = messageEntity || selectEntity(state)
        invariant(entity, 'entity must be truthy')
        origin === Origin.LIST_ITEM && dispatch(setLastClickedEntity(entity))

        const { isTourDisabled, hours, tz } = entity

        const equalCTAIntent = isTourDisabled ? MESSAGE : TOUR
        const hasSelectedTime = selectHasSelectedTime(state)

        if (equalCTAIntent === TOUR && !hasSelectedTime && !hasInstantTourAvailability) {
          const twoDaysAhead = getTwoDaysAhead(new Date(), tz)
          invariant(twoDaysAhead != null, 'invalid date')
          const twoOrThreeDaysAhead =
            getDayInTz(twoDaysAhead, tz) === 0 ? getOneDayAhead(twoDaysAhead, tz) : twoDaysAhead
          invariant(twoOrThreeDaysAhead, 'invalid date')
          const defaultTime = twoOrThreeDaysAhead

          let earliestTime = defaultTime
          if (hours?.length) {
            const officeHoursEarliestTime = getNextPossibleTimeOfficeHours(hours, Date.now(), tz)
            if (officeHoursEarliestTime) {
              earliestTime = officeHoursEarliestTime
            }
          }
          dispatch(receiveSelectedDatetime(earliestTime))
        }
        dispatch(
          openMessageModalWithIntent({
            analyticChannel,
            currentTab: ownProps?.extraPayload?.currentTab,
            entity,
            equalCTAIntent,
            hasInstantTourAvailability,
            origin,
            selectedFloorplan,
            selectedUnit,
            showDatePicker: true,
          })
        )
      })
    },
    submitTour: async (data: NonNullable<ReturnType<ReturnType<typeof getOneTapTourData>>>) => {
      await dispatch(
        createMultifamilyTourRequest({ analyticChannel: ownProps.analyticChannel, ...data })
      )
      const { entity, origin, formValues, oneTap } = data

      origin === Origin.LIST_ITEM && dispatch(setLastClickedEntity(ownProps?.entity || {}))

      return dispatch(
        trackTourBookingEvent({
          entity,
          oneTap,
          name: TOUR_BOOKING_SUBMIT_CLICK,
          origin,
          pathname: window.location.pathname,
          values: formValues,
          ...ownProps?.extraPayload,
        })
      )
    },
    trackTourClick: () => {
      dispatch((_, getState) => {
        const state = getState()
        const entity: Listable | Pin<RawPinInventorySearch> | MaybeEmptyProperty =
          ownProps.entity || selectEntity(state) || {}
        dispatch(mixpanelClickTourAvailability(entity, TOUR_BUTTON_TYPES.REQUEST_A_TOUR))
        const user = getUser(state)
        const entityProperties = getEntityProperties(entity, state)
        const props = {
          origin: ownProps.origin,
          ...entityProperties,
          pageType: getTraktorPageType(entity, ownProps.match, state),
          ...ownProps?.extraPayload,
        }
        dispatch(event({ name: CLICK_TOUR_AVAILABILITY, user, props }))
      })
    },
    trackUndoClick: () => {
      dispatch((_, getState) => {
        const state = getState()
        const entity: Listable | MaybeEmptyProperty | Pin<RawPinInventorySearch> =
          ownProps.entity || selectEntity(state) || {}
        const origin = ownProps.origin
        const isMobile = selectIsMobile(state)
        const pageName = getPageNameFromRouteProps({ routeProps: ownProps, isMobile })

        track({
          event: EVENT_NAMES.UNDO_TOUR,
          payload: {
            [PROP_NAMES.PREVIOUS_PAGE]: getPreviousPage(),
            ...getCommonProps({ state, entity, pageName, origin }),
          },
        })

        const user = getUser(state)
        const entityProperties = getEntityProperties(entity, state)
        const props = {
          origin,
          ...entityProperties,
          pageType: getTraktorPageType(entity, ownProps.match, state),
        }
        dispatch(event({ name: UNDO_TOUR, user, props }))
      })
    },
    setSendingTour: (isSending: boolean) => {
      dispatch(setSendingTour(isSending))
    },
  } satisfies Partial<TourButtonProps>
}

const equalProps = ['analyticChannel' as const, 'origin' as const, 'selectedUnit' as const]
const shallowEqualProps = ['entity' as const]
export const memoMapDispatchToProps = memo(mapDispatchToProps, equalProps, shallowEqualProps)

export const TourButtonContainer = hocCompose(
  withRouter,
  connect(mapStateToProps, memoMapDispatchToProps),
  TourButton
)
